import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';

import type { AnalyticsSnapshot, BetaAccess, BetaUsage } from './analyticsApi';
import { BETA_ACCESS_ACTIONS, type BetaAccessActions } from './betaAccessApi';
import { ANALYTICS } from './analyticsTokens';
import { BETA_FEATURES, type BetaFeatureSpec, type StageTone } from './betaFeatures';
import { trendSeries } from './betaTrendGeometry';
import {
  accessCount,
  chartPeriods,
  figureOf,
  firstActivityDay,
  grantSubs,
  peopleActiveSeries,
  rangeBounds,
  rangeSpec,
  reconcile,
  type AccessIndex,
  type Change,
  type CountKey,
  type RangeBounds,
  type RangeKey,
} from './betaUsageRanges';
import { betaAccounts, betaUserRows, filterBetaRows, rosterAccess, shownFeatures, type BetaCell, type BetaUserRow } from './betaUsageTable';
import { BetaUsageTrend } from './BetaUsageTrend';
import { fetchImpersonationCandidates, filterCandidates, type ImpersonationCandidate, type ImpersonationCandidateList } from './impersonationApi';

type Props = {
  snapshot: AnalyticsSnapshot;
  range: RangeKey;
  count?: CountKey;
  rosterSource?: () => Promise<ImpersonationCandidateList>;
  accessActions?: BetaAccessActions;
  /** Called after any change to the beta list, so the page can read it again. */
  onAccessChanged?: () => void;
  now?: () => Date;
};

type ListEditing = { access: BetaAccess; actions: BetaAccessActions; onChanged: () => void };

type Roster = { known: true; users: ImpersonationCandidate[] } | { known: false };

const base = { fontFamily: ANALYTICS.font } as const;

const bodyText = { ...base, fontSize: 16, lineHeight: '24px', color: ANALYTICS.muted } as const;

const smallText = { ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.disabled } as const;

const eyebrow = { ...base, fontSize: 12, lineHeight: '16px', fontWeight: 700, letterSpacing: ANALYTICS.tracking.wider, textTransform: 'uppercase', color: ANALYTICS.muted } as const;

const heading = { ...base, fontSize: 20, lineHeight: '28px', fontWeight: 700, color: ANALYTICS.ink } as const;

const cardSx = {
  bgcolor: ANALYTICS.surface,
  borderRadius: '8px',
  border: `1px solid ${ANALYTICS.grey50}`,
} as const;

const STAGE_TONE: Record<StageTone, { color: string; background: string }> = {
  green: { color: ANALYTICS.green700, background: ANALYTICS.green50 },
  orange: { color: ANALYTICS.orange800, background: ANALYTICS.orange50 },
  grey: { color: ANALYTICS.muted, background: ANALYTICS.grey25 },
};

export function BetaFeaturesTab({
  snapshot,
  range,
  count = 'all',
  rosterSource = fetchImpersonationCandidates,
  accessActions = BETA_ACCESS_ACTIONS,
  onAccessChanged = () => {},
  now = () => new Date(),
}: Props) {
  const [roster, setRoster] = useState<Roster | null>(null);

  useEffect(() => {
    let cancelled = false;
    rosterSource()
      .then((list) => {
        if (!cancelled) setRoster(list.available ? { known: true, users: list.users } : { known: false });
      })
      .catch(() => {
        if (!cancelled) setRoster({ known: false });
      });
    return () => {
      cancelled = true;
    };
  }, [rosterSource]);

  const usage = snapshot.computed ? snapshot.betaUsage : undefined;
  if (!usage) {
    return (
      <Typography data-testid="beta-usage-uncounted" sx={bodyText}>
        Beta usage has not been computed yet. It is refreshed every 20 minutes.
      </Typography>
    );
  }
  if (count === 'list' && snapshot.betaAccess === null) {
    return (
      <Typography data-testid="beta-usage-access-missing" sx={bodyText}>
        The beta list could not be loaded. Refresh to try again.
      </Typography>
    );
  }
  if (roster === null) return <CircularProgress data-testid="beta-usage-loading" size={24} />;
  const list = count === 'list' ? snapshot.betaAccess : null;
  const reconciled = reconcile(list ?? rosterAccess(roster.known ? roster.users : null, usage), usage);
  return (
    <Loaded
      usage={reconciled.usage}
      index={reconciled.index}
      range={range}
      count={count}
      roster={roster}
      editing={list ? { access: list, actions: accessActions, onChanged: onAccessChanged } : null}
      asOf={asOfDate(snapshot.computedAt, now)}
    />
  );
}

function asOfDate(computedAt: string | undefined, now: () => Date): Date {
  const at = computedAt ? new Date(computedAt) : null;
  return at && !Number.isNaN(at.getTime()) ? at : now();
}

type LoadedProps = {
  usage: BetaUsage;
  index: AccessIndex;
  range: RangeKey;
  count: CountKey;
  roster: Roster;
  editing: ListEditing | null;
  asOf: Date;
};

function Loaded({ usage, index, range, count, roster, editing, asOf }: LoadedProps) {
  const spec = rangeSpec(range);
  const bounds = rangeBounds(range, asOf);
  const firstActivity = new Map(BETA_FEATURES.map((feature) => [feature.id, firstActivityDay(usage, feature.metrics)]));
  const periods = chartPeriods(spec.unit, bounds.selected, usage);
  const trend = trendSeries(periods, BETA_FEATURES.map((feature) => [feature, peopleActiveSeries(usage, index, feature, periods, spec.unit, bounds.selected)]));
  const sections = useRef(new Map<string, HTMLElement>());
  const withAccess = (featureId: string) => (count === 'list' || roster.known ? accessCount(index, featureId) : null);

  return (
    <>
      <Typography data-testid="beta-usage-scope" sx={smallText}>
        District users only. Flashlight staff and impersonated sessions are not counted. Days end at midnight UTC, and today counts activity so far.
      </Typography>
      {usage.unattributed > 0 && (
        <Typography data-testid="beta-usage-unattributed" sx={bodyText}>
          {usage.unattributed === 1
            ? '1 beta event could not be tied to a person and is not counted.'
            : `${usage.unattributed} beta events could not be tied to a person and are not counted.`}
        </Typography>
      )}
      <Box component="section" aria-label="By feature" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography sx={eyebrow}>By feature</Typography>
        <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {BETA_FEATURES.map((feature) => (
            <SummaryCard
              key={feature.id}
              feature={feature}
              figure={figureOf(usage, index, feature.id, feature.keyAction2, 'people', bounds, firstActivity.get(feature.id) ?? null)}
              withAccess={withAccess(feature.id)}
              onJump={() => sections.current.get(feature.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          ))}
        </Box>
      </Box>
      <BetaUsageTrend trend={trend} unit={spec.unit} />
      <Typography sx={{ ...eyebrow, pt: '16px' }}>Feature detail</Typography>
      {BETA_FEATURES.map((feature) => (
        <FeatureSection
          key={feature.id}
          ref={(element) => {
            if (element) sections.current.set(feature.id, element);
            else sections.current.delete(feature.id);
          }}
          feature={feature}
          usage={usage}
          index={index}
          bounds={bounds}
          withAccess={withAccess(feature.id)}
          rangeLabel={spec.label}
          compare={spec.compare}
          firstActivity={firstActivity.get(feature.id) ?? null}
        />
      ))}
      <UsersCard usage={usage} index={index} count={count} roster={roster} editing={editing} bounds={bounds} />
    </>
  );
}

function StageChip({ stage, tone }: { stage: string; tone: StageTone }) {
  return (
    <Box
      component="span"
      data-testid="beta-usage-stage"
      sx={{
        ...base,
        display: 'inline-block',
        padding: '0 8px',
        borderRadius: '4px',
        fontSize: 12,
        lineHeight: '20px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        color: STAGE_TONE[tone].color,
        bgcolor: STAGE_TONE[tone].background,
      }}
    >
      {stage}
    </Box>
  );
}

function ChangeMark({ change }: { change: Change }) {
  if (change.kind === 'none') return null;
  const value = change.kind === 'delta' ? change.value : 0;
  const kind = change.kind === 'unknown' || value === 0 ? 'flat' : value > 0 ? 'up' : 'down';
  const color = { up: ANALYTICS.green600, down: ANALYTICS.red600, flat: ANALYTICS.disabled }[kind];
  return (
    <Box
      component="span"
      data-testid="beta-usage-change"
      data-kind={kind}
      title={change.kind === 'unknown' ? 'No activity before this range' : undefined}
      sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: kind === 'flat' ? 500 : 700, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
    >
      {kind === 'up' ? `↑ ${value}` : kind === 'down' ? `↓ ${-value}` : '—'}
    </Box>
  );
}

type SummaryCardProps = {
  feature: BetaFeatureSpec;
  figure: { value: number; change: Change };
  /** Null when the Insight user list could not be read, so there is no "of N". */
  withAccess: number | null;
  onJump: () => void;
};

function SummaryCard({ feature, figure, withAccess, onJump }: SummaryCardProps) {
  return (
    <Box
      component="button"
      type="button"
      data-testid="beta-usage-card"
      data-feature={feature.id}
      aria-label={`${feature.name}: jump to detail`}
      onClick={onJump}
      sx={{
        ...cardSx,
        ...base,
        flex: 1,
        minWidth: 200,
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        cursor: 'pointer',
        textAlign: 'left',
        color: ANALYTICS.ink,
        '&:hover': { borderColor: ANALYTICS.grey100 },
        '&:focus-visible': { outline: `2px solid ${ANALYTICS.primary}`, outlineOffset: '2px' },
      }}
    >
      <StageChip stage={feature.stage} tone={feature.stageTone} />
      <Typography data-testid="beta-usage-card-name" sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 700, whiteSpace: 'nowrap', mt: '4px' }}>
        {feature.name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mt: '8px' }}>
        <Typography
          data-testid="beta-usage-card-figure"
          sx={{ ...base, fontSize: 48, lineHeight: '48px', fontWeight: 800, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}
        >
          {figure.value}
        </Typography>
        <ChangeMark change={figure.change} />
      </Box>
      <Typography data-testid="beta-usage-card-of" sx={{ ...bodyText, whiteSpace: 'nowrap' }}>
        {withAccess === null ? `people ${feature.headline}` : `of ${withAccess} people ${feature.headline}`}
      </Typography>
    </Box>
  );
}

type FeatureSectionProps = {
  feature: BetaFeatureSpec;
  usage: BetaUsage;
  index: AccessIndex;
  bounds: RangeBounds;
  withAccess: number | null;
  rangeLabel: string;
  compare: string | null;
  firstActivity: string | null;
  ref: (element: HTMLElement | null) => void;
};

const metricRow = { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 0', borderBottom: `1px solid ${ANALYTICS.divider}` } as const;

const valueColumn = { width: 136, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '4px' } as const;

const changeColumn = { width: 160, flexShrink: 0, textAlign: 'right' } as const;

function FeatureSection({ feature, usage, index, bounds, withAccess, rangeLabel, compare, firstActivity, ref }: FeatureSectionProps) {
  return (
    <Box
      ref={ref}
      component="section"
      data-testid="beta-usage-section"
      data-feature={feature.id}
      aria-label={feature.name}
      sx={{ ...cardSx, padding: '24px 24px 8px', display: 'flex', flexDirection: 'column', scrollMarginTop: '24px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pb: '20px', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={heading}>
          {feature.name}
        </Typography>
        <StageChip stage={feature.stage} tone={feature.stageTone} />
        {withAccess !== null && (
          <Typography data-testid="beta-usage-access-count" sx={{ ...bodyText, ml: 'auto' }}>
            {withAccess} users with access
          </Typography>
        )}
      </Box>
      <Box sx={{ ...metricRow, padding: '0 0 8px', borderBottom: `2px solid ${ANALYTICS.ink}`, alignItems: 'flex-end' }}>
        <Typography sx={{ ...base, flex: 1, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>Action</Typography>
        <Typography data-testid="beta-usage-range-head" sx={{ ...base, ...valueColumn, display: 'block', textAlign: 'right', fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>
          {rangeLabel}
        </Typography>
        {compare && (
          <Typography data-testid="beta-usage-compare-head" sx={{ ...base, ...changeColumn, fontSize: 12, lineHeight: '16px', fontWeight: 700, color: ANALYTICS.muted }}>
            {compare}
          </Typography>
        )}
      </Box>
      {feature.groups.map((group, groupIndex) => (
        <Box key={group.label ?? groupIndex}>
          {group.label && (
            <Typography
              data-testid="beta-usage-group-label"
              sx={{ ...eyebrow, fontWeight: 800, color: ANALYTICS.primary, padding: groupIndex === 0 ? '20px 0 4px' : '24px 0 4px', borderTop: groupIndex === 0 ? 'none' : `1px solid ${ANALYTICS.divider}` }}
            >
              {group.label}
            </Typography>
          )}
          {group.rows.map((row) => {
            const figure = figureOf(usage, index, feature.id, row.metric, row.kind, bounds, firstActivity);
            return (
              <Box key={row.metric} data-testid="beta-usage-metric-row" sx={{ ...metricRow, '&:last-child': { borderBottom: 0 } }}>
                <Typography data-testid="beta-usage-metric-label" sx={{ ...base, flex: 1, minWidth: 0, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink }}>
                  {row.label}
                </Typography>
                <Box data-testid="beta-usage-metric-value" sx={valueColumn}>
                  <Box
                    component="strong"
                    sx={{ ...base, fontSize: 20, lineHeight: '24px', fontWeight: figure.value === 0 ? 500 : 700, color: figure.value === 0 ? ANALYTICS.disabled : ANALYTICS.ink, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {figure.value}
                  </Box>
                  <Box component="small" sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
                    {row.unit}
                  </Box>
                </Box>
                {compare && (
                  <Box sx={changeColumn}>
                    <ChangeMark change={figure.change} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

type UsersCardProps = {
  usage: BetaUsage;
  index: AccessIndex;
  count: CountKey;
  roster: Roster;
  editing: ListEditing | null;
  bounds: RangeBounds;
};

function UsersCard({ usage, index, count, roster, editing, bounds }: UsersCardProps) {
  const [account, setAccount] = useState<string | null>(null);
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = betaUserRows(usage, index, roster.known ? roster.users : null, bounds.selected, BETA_FEATURES, count);
  const remove = editing
    ? (row: BetaUserRow) => {
        setError(null);
        editing.actions
          .remove(row.email !== '' ? { email: row.email } : { subs: grantSubs(index, row.sub) })
          .catch(() => setError(`Could not remove ${row.user} from the beta list. Try again.`))
          .finally(editing.onChanged);
      }
    : undefined;
  const features = shownFeatures(BETA_FEATURES, featureId);
  return (
    <Box component="section" aria-label="Beta users" sx={{ ...cardSx, padding: '24px 24px 8px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={heading}>
          Beta users
        </Typography>
        {rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Filter label="Account" value={account} all="All accounts" options={betaAccounts(rows).map((name) => ({ value: name, label: name }))} onChange={setAccount} />
            <Filter label="Feature" value={featureId} all="All features" options={BETA_FEATURES.map((feature) => ({ value: feature.id, label: feature.name }))} onChange={setFeatureId} />
          </Box>
        )}
      </Box>
      {editing && roster.known && <AddToBeta people={roster.users} editing={editing} onError={setError} />}
      {editing && !roster.known && (
        <Typography data-testid="beta-list-add-unavailable" sx={bodyText}>
          People can be added once the user list loads. Refresh to try again.
        </Typography>
      )}
      {error && (
        <Typography data-testid="beta-list-error" role="alert" sx={{ ...bodyText, color: ANALYTICS.red600 }}>
          {error}
        </Typography>
      )}
      {!roster.known && (
        <Typography data-testid="beta-usage-roster-missing" sx={{ ...bodyText, color: ANALYTICS.yellow900, bgcolor: ANALYTICS.yellow100, borderRadius: '8px', padding: '12px 16px' }}>
          The user list could not be loaded, so names, accounts and roles are missing.
        </Typography>
      )}
      {rows.length === 0 && (
        <Typography data-testid="beta-usage-empty" sx={{ ...bodyText, pb: '16px' }}>
          {count === 'list' ? 'Nobody is on the beta list yet.' : 'No beta activity has been recorded yet.'}
        </Typography>
      )}
      {rows.length > 0 && <UsersTable rows={filterBetaRows(rows, account)} features={features} onRemove={remove} />}
    </Box>
  );
}

type FilterProps = {
  label: string;
  value: string | null;
  all: string;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
};

function Filter({ label, value, all, options, onChange }: FilterProps) {
  return (
    <Box
      component="select"
      aria-label={label}
      value={value ?? ''}
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value === '' ? null : event.target.value)}
      sx={{
        ...base,
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 600,
        color: ANALYTICS.ink,
        padding: '4px 12px',
        borderRadius: '4px',
        border: `1px solid ${ANALYTICS.grey100}`,
        bgcolor: ANALYTICS.surface,
        cursor: 'pointer',
        '&:hover': { borderColor: ANALYTICS.muted },
      }}
    >
      <option value="">{all}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Box>
  );
}

const headerCell = {
  ...base,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 700,
  color: ANALYTICS.muted,
  textAlign: 'left',
  padding: '0 0 8px',
  borderBottom: `2px solid ${ANALYTICS.ink}`,
  verticalAlign: 'bottom',
  whiteSpace: 'nowrap',
} as const;

const bodyCell = {
  ...base,
  fontSize: 16,
  lineHeight: '24px',
  color: ANALYTICS.ink,
  padding: '12px 0',
  borderBottom: `1px solid ${ANALYTICS.divider}`,
  verticalAlign: 'middle',
} as const;

const visuallyHidden = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

const textButton = {
  ...base,
  border: 0,
  bgcolor: 'transparent',
  cursor: 'pointer',
  padding: 0,
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 600,
  color: ANALYTICS.primary,
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${ANALYTICS.primary}`, outlineOffset: '2px' },
} as const;

const firstOfFeature = { borderLeft: `1px solid ${ANALYTICS.divider}`, paddingLeft: '12px' } as const;

const lastOfFeature = { paddingRight: '12px' } as const;

type UsersTableProps = {
  rows: BetaUserRow[];
  features: readonly BetaFeatureSpec[];
  /** Present only while counting the beta list, which is the only list that can be edited here. */
  onRemove?: (row: BetaUserRow) => void;
};

function UsersTable({ rows, features, onRemove }: UsersTableProps) {
  const columns = 2 + features.length * 2 + (onRemove ? 1 : 0);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" data-testid="beta-usage-users" sx={{ borderCollapse: 'collapse', width: '100%' }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="td" colSpan={2} />
            {features.map((feature) => (
              <Box
                component="td"
                key={feature.id}
                colSpan={2}
                data-testid="beta-usage-feature"
                sx={{ ...base, ...firstOfFeature, ...lastOfFeature, fontSize: 12, lineHeight: '16px', fontWeight: 800, color: ANALYTICS.primary, paddingBottom: '4px' }}
              >
                {feature.short}
              </Box>
            ))}
            {onRemove && <Box component="td" />}
          </Box>
          <Box component="tr">
            <HeaderCell>User</HeaderCell>
            <HeaderCell>Account</HeaderCell>
            {features.flatMap((feature) =>
              feature.columns.map((column, position) => (
                <HeaderCell key={`${feature.id}-${column.metric}`} numeric edge={position === 0 ? 'first' : 'last'}>
                  {column.label}
                </HeaderCell>
              )),
            )}
            {onRemove && (
              <Box component="th" scope="col" sx={headerCell}>
                <Box component="span" sx={visuallyHidden}>
                  Remove
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.length === 0 && (
            <Box component="tr">
              <Box component="td" colSpan={columns} sx={{ ...bodyText, padding: '32px 0', textAlign: 'center', borderBottom: 0 }}>
                No beta users in this account.
              </Box>
            </Box>
          )}
          {rows.map((row) => (
            <Box component="tr" key={row.sub} data-testid="beta-usage-row" sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <Box component="td" sx={{ ...bodyCell, minWidth: 200 }}>
                <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
                  {row.user}
                </Box>
                {row.email !== '' && row.email !== row.user && (
                  <Box component="span" sx={{ display: 'block', fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
                    {row.email}
                  </Box>
                )}
              </Box>
              <Box component="td" sx={bodyCell}>
                <Box component="span" sx={{ display: 'block' }}>
                  {row.account}
                </Box>
                {row.role !== '' && (
                  <Box component="span" sx={{ display: 'block', fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
                    {row.role}
                  </Box>
                )}
              </Box>
              {features.flatMap((feature) => featureCells(feature, row.cells[feature.id] ?? null))}
              {onRemove && (
                <Box component="td" sx={{ ...bodyCell, textAlign: 'right', pl: '12px' }}>
                  <Box component="button" type="button" aria-label={`Remove ${row.user} from the beta list`} onClick={() => onRemove(row)} sx={textButton}>
                    Remove
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function HeaderCell({ children, numeric = false, edge }: { children: ReactNode; numeric?: boolean; edge?: 'first' | 'last' }) {
  return (
    <Box
      component="th"
      scope="col"
      sx={{ ...headerCell, ...(edge === 'first' ? firstOfFeature : {}), ...(edge === 'last' ? lastOfFeature : {}), textAlign: numeric ? 'right' : 'left' }}
    >
      {children}
    </Box>
  );
}

function featureCells(feature: BetaFeatureSpec, cell: BetaCell): ReactNode[] {
  return feature.columns.map((column, position) => {
    const value = cell === null ? null : (cell[column.metric] ?? 0);
    return (
      <Box
        component="td"
        key={`${feature.id}-${column.metric}`}
        title={value === null ? 'Not counted: not a user in a district' : undefined}
        sx={{
          ...bodyCell,
          ...(position === 0 ? firstOfFeature : lastOfFeature),
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: value ? 700 : 400,
          color: value ? ANALYTICS.ink : ANALYTICS.disabled,
        }}
      >
        {value === null ? '—' : value}
      </Box>
    );
  });
}

const MAX_MATCHES = 5;

type AddToBetaProps = {
  people: ImpersonationCandidate[];
  editing: ListEditing;
  onError: (message: string | null) => void;
};

/** Only Insight users in a district can be granted, so only they are offered, and nobody already on the list. */
function AddToBeta({ people, editing, onError }: AddToBetaProps) {
  const [query, setQuery] = useState('');
  const subs = new Set(editing.access.entries.map((entry) => entry.sub));
  const userIds = new Set(editing.access.entries.flatMap((entry) => (entry.userId ? [entry.userId] : [])));
  const eligible = people.filter((person) => person.districtId !== '' && !subs.has(person.sub) && !(person.userId && userIds.has(person.userId)));
  const matches = query.trim() ? filterCandidates(eligible, query).slice(0, MAX_MATCHES) : [];
  const add = (person: ImpersonationCandidate) => {
    const name = person.name || person.email;
    onError(null);
    editing.actions
      .add(person.email)
      .then(() => setQuery(''))
      .catch(() => onError(`Could not add ${name} to the beta. Try again.`))
      .finally(editing.onChanged);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: 480 }}>
      <TextField label="Add to beta" size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name or email" fullWidth />
      {matches.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, ...cardSx }}>
          {matches.map((person) => (
            <Box
              component="li"
              key={person.sub}
              data-testid="beta-list-match"
              sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderBottom: `1px solid ${ANALYTICS.divider}`, '&:last-child': { borderBottom: 0 } }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ ...base, display: 'block', fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink }}>
                  {person.name || person.email}
                </Box>
                <Box component="span" sx={{ ...base, display: 'block', fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted }}>
                  {person.districtName ? `${person.email} · ${person.districtName}` : person.email}
                </Box>
              </Box>
              <Box component="button" type="button" aria-label={`Add ${person.name || person.email} to the beta`} onClick={() => add(person)} sx={textButton}>
                Add
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
